import React, { useState } from 'react';
import { useBrandPitches, BRAND_PITCH_STATUSES } from '../context/BrandPitchesContext';
import { Star, Trash2, X, Send, GripVertical } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from './ui/select';
import { toast } from 'sonner';

export const StatusPill = ({ status, onChange, canEdit }) => {
  const s = BRAND_PITCH_STATUSES.find((x) => x.id === status) || BRAND_PITCH_STATUSES[0];
  if (!canEdit) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
        {s.label}
      </span>
    );
  }
  return (
    <Select value={status} onValueChange={onChange}>
      <SelectTrigger className="h-6 px-2 py-0 text-[11px] w-auto min-w-[110px] bg-slate-100 border-0" data-testid="bp-status-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {BRAND_PITCH_STATUSES.map((x) => (
          <SelectItem key={x.id} value={x.id}>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: x.color }} />
              {x.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const Stars = ({ value = 0, onChange, size = 'sm', testId = 'bp-stars' }) => {
  const [hover, setHover] = useState(0);
  const px = size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  return (
    <div className="inline-flex items-center gap-0.5" onMouseLeave={() => setHover(0)} data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange?.(n === value ? 0 : n); }}
            onMouseEnter={() => setHover(n)}
            className="p-0.5 leading-none"
            title={`${n} star${n === 1 ? '' : 's'}`}
            disabled={!onChange}
            data-testid={`${testId}-${n}`}
          >
            <Star className={`${px} ${filled ? 'fill-pink-400 text-pink-400' : 'text-slate-300'}`} />
          </button>
        );
      })}
    </div>
  );
};

export const fmtRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const days = Math.floor((now - d) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

/**
 * Shared row used by TalentPitchDashboard, BrandPitchTalentsList, and PitchingMatrix.
 *
 * Props:
 *   row: brand_pitch record
 *   leftContent: JSX shown on the left (brand or talent avatar+name)
 *   canEdit: manager?
 *   draggable / dragHandlers: optional HTML5 drag props for reordering
 *   showBrandInline (optional): for matrix view, adds a small tag under the leftContent
 */
export const PitchRow = ({
  row,
  leftContent,
  canEdit,
  onStars,
  onStatus,
  onRemove,
  onAddComms,
  onRemoveComms,
  draggable = false,
  dragHandlers,
  dropShadow,
  hideStars = false,
  rightExtra,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const commsCount = (row.comms || []).length;
  const latestComms = commsCount ? row.comms[commsCount - 1] : null;

  const submitNote = async () => {
    const t = newNote.trim();
    if (!t) return;
    setSaving(true);
    try {
      await onAddComms(t);
      setNewNote('');
      setExpanded(true);
    } catch { toast.error('failed to add note'); }
    finally { setSaving(false); }
  };

  return (
    <li
      className={`p-4 hover:bg-slate-50/60 transition ${dropShadow ? 'ring-2 ring-pink-200' : ''}`}
      data-testid={`pitch-row-${row.id}`}
      {...(dragHandlers || {})}
    >
      <div className="flex items-center gap-3">
        {draggable && (
          <div className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 shrink-0" title="drag to reorder">
            <GripVertical className="w-4 h-4" />
          </div>
        )}
        {leftContent}
        <div className="flex items-center gap-2 flex-wrap ml-auto">
          {rightExtra}
          <StatusPill status={row.status} canEdit={canEdit} onChange={onStatus} />
          {!hideStars && <Stars value={row.stars || 0} onChange={canEdit ? onStars : undefined} testId={`row-stars-${row.id}`} />}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          className="text-xs text-pink-700 hover:text-pink-800 whitespace-nowrap"
          data-testid={`toggle-comms-${row.id}`}
        >
          {expanded ? 'hide' : `${commsCount} note${commsCount === 1 ? '' : 's'}`}
        </button>
        {canEdit && onRemove && (
          <button onClick={onRemove} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="remove">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {!expanded && latestComms && (
        <div className="text-xs text-slate-500 mt-1.5 pl-11 truncate">
          <span className="text-slate-400">{fmtRelative(latestComms.at)} · {latestComms.by_name || 'unknown'}:</span> {latestComms.text}
        </div>
      )}

      {expanded && (
        <div className="mt-3 pl-11 space-y-2" data-testid={`comms-thread-${row.id}`}>
          {(row.comms || []).length === 0 && (
            <div className="text-xs text-slate-400 italic">no notes yet</div>
          )}
          {(row.comms || []).map((c) => (
            <div key={c.id} className="group flex items-start gap-2 text-xs bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
              <div className="flex-1 min-w-0">
                <div className="text-slate-400">
                  {new Date(c.at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · <span className="text-slate-500">{c.by_name || 'unknown'}</span>
                </div>
                <div className="text-slate-700 whitespace-pre-wrap mt-0.5">{c.text}</div>
              </div>
              {canEdit && (
                <button onClick={() => onRemoveComms(c.id)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-600" title="delete note">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          {canEdit && (
            <div className="flex items-start gap-2 mt-2">
              <Input
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitNote(); } }}
                placeholder="add an update… (Enter to save)"
                className="h-9 text-sm flex-1"
                data-testid={`comms-input-${row.id}`}
              />
              <Button size="sm" onClick={submitNote} disabled={!newNote.trim() || saving} className="h-9 bg-pink-500 hover:bg-pink-600 text-white">
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}
    </li>
  );
};
